import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import PincodeModal from "@/components/PincodeModal";
import GuestLoginPrompt from "@/components/GuestLoginPrompt";
import { ArrowLeft, Plus, Minus, Trash, Lightning, MapPin, User } from "@phosphor-icons/react";

export default function CartPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { getStoreCart, add, decrement, remove, subtotal, pincode, setPincode } = useCart();
  const [store, setStore] = useState(null);
  const [pincodeInfo, setPincodeInfo] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [liveInv, setLiveInv] = useState({}); // product_id -> current inventory

  useEffect(() => {
    api.get(`/storefront/${slug}`).then((r) => setStore(r.data));
    if (pincode && pincode.length === 6) {
      api.get(`/storefront/${slug}/pincode/${pincode}`).then((r) => setPincodeInfo(r.data));
    } else {
      setPincodeInfo(null);
    }
  }, [slug, pincode]);

  // Refetch live inventory for every cart item so we stay in sync with what the
  // seller has updated on their side or other concurrent orders.
  const cartForInv = getStoreCart(slug);
  useEffect(() => {
    let active = true;
    (async () => {
      const next = {};
      await Promise.all(cartForInv.items.map(async (it) => {
        try {
          const r = await api.get(`/storefront/${slug}/product/${it.product_id}`);
          next[it.product_id] = Number(r.data.inventory ?? 0);
        } catch {
          next[it.product_id] = null;
        }
      }));
      if (active) setLiveInv(next);
    })();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [slug, cartForInv.items.length]);

  if (!store) return <div className="p-8">Loading…</div>;

  const cart = getStoreCart(slug);
  const sub = subtotal(slug);
  const settings = store.settings || {};
  const freeAbove = Number(settings.free_delivery_threshold || 0);
  const deliveryFee = Number(settings.self_delivery_charge || 0);
  const minOrder = Number(settings.min_order_value || 0);
  const availability = store.availability_status || { is_open: true };
  const storeClosed = !availability.is_open;
  const serviceable = !!pincodeInfo?.serviceable;
  const charge = serviceable && !storeClosed
    ? (freeAbove > 0 && sub >= freeAbove ? 0 : deliveryFee)
    : 0;
  const total = sub + charge;
  const belowMin = minOrder > 0 && sub < minOrder;

  // Stock issues: items whose qty > current live inventory (or item is OOS)
  const stockIssues = cart.items
    .map((it) => {
      const avail = liveInv[it.product_id];
      if (avail === undefined || avail === null) return null; // not loaded yet
      if (avail <= 0) return { item: it, avail, reason: "oos" };
      if (it.qty > avail) return { item: it, avail, reason: "low" };
      return null;
    })
    .filter(Boolean);
  const hasStockIssue = stockIssues.length > 0;

  // Action needed next (used for label + click handler). null = ready to checkout.
  const actionNeeded =
    cart.items.length === 0 ? "empty"
    : hasStockIssue ? "stock"
    : !pincode ? "set-pincode"
    : !serviceable ? "change-pincode"
    : storeClosed ? "closed"
    : belowMin ? "min"
    : !user ? "login"
    : null;

  const buttonLabel = {
    "empty": "Cart is empty",
    "stock": "Update items to continue",
    "set-pincode": "Set delivery pincode",
    "change-pincode": `Not serviceable at ${pincode}`,
    "closed": `Store closed · ${availability.reason || ""}`,
    "min": `Add ₹${(minOrder - sub).toFixed(0)} more`,
    "login": "Login & checkout →",
    null: "Proceed to checkout →",
  }[actionNeeded];

  // Disable only when there's nothing the user can do via the CTA itself.
  const buttonDisabled = actionNeeded === "empty" || actionNeeded === "closed" || actionNeeded === "min" || actionNeeded === "stock";

  const onCheckoutClick = () => {
    if (cart.items.length === 0) return;
    if (actionNeeded === "set-pincode" || actionNeeded === "change-pincode") {
      setShowPinModal(true); return;
    }
    if (actionNeeded === "closed") { toast.error(availability.reason || "Store is currently closed"); return; }
    if (actionNeeded === "min") { toast.error(`Add ₹${(minOrder - sub).toFixed(0)} more to reach minimum order`); return; }
    if (actionNeeded === "login") {
      sessionStorage.setItem("qd_return_to", `/${slug}/checkout`);
      toast.message("Please login to complete your order");
      nav("/login");
      return;
    }
    nav(`/${slug}/checkout`);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] pb-40">
      <header className="sticky top-0 z-40 glass border-b border-gray-100 px-4 py-3 flex items-center gap-2">
        <Link to={`/${slug}`} className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-testid="cart-back">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black truncate">Your cart</div>
          <div className="text-xs text-gray-500">{cart.items.length} item{cart.items.length !== 1 && "s"} · {store.name}</div>
        </div>
        {!user && (
          <Link to="/login"
            onClick={() => sessionStorage.setItem("qd_return_to", `/${slug}/cart`)}
            className="inline-flex items-center gap-1 bg-[#15803D] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#166534]"
            data-testid="cart-login-cta">
            <User size={12} weight="bold" /> Login
          </Link>
        )}
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {cart.items.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-3">🛒</div>
            <div className="font-heading font-black text-xl">Cart is empty</div>
            <Link to={`/${slug}`} className="qd-btn-primary inline-block mt-4">Start shopping</Link>
          </div>
        ) : (
          <>
            {/* Delivery / location card */}
            <button onClick={() => setShowPinModal(true)}
              data-testid="cart-set-pincode"
              className="w-full qd-card p-4 flex items-center gap-3 bg-[#F8CB46]/10 border-[#F8CB46]/40 text-left">
              <Lightning weight="fill" size={20} className="text-[#15803D]" />
              <div className="flex-1 min-w-0">
                {!pincode && (
                  <>
                    <div className="font-heading font-bold text-sm">Set delivery pincode</div>
                    <div className="text-xs text-gray-600">Tap to enter pincode or auto-detect</div>
                  </>
                )}
                {pincode && serviceable && !storeClosed && (
                  <>
                    <div className="font-heading font-bold text-sm">Arriving in {pincodeInfo.eta_minutes} minutes</div>
                    <div className="text-xs text-gray-600 flex items-center gap-1"><MapPin size={12} /> {pincodeInfo.area} · {pincode}</div>
                  </>
                )}
                {pincode && serviceable && storeClosed && (
                  <>
                    <div className="font-heading font-bold text-sm text-red-700">Store closed</div>
                    <div className="text-xs text-gray-600">Opens at {availability.open_time || "09:00"} · {pincode}</div>
                  </>
                )}
                {pincode && !serviceable && (
                  <>
                    <div className="font-bold text-sm text-amber-700">Not deliverable to {pincode}</div>
                    <div className="text-xs text-gray-600">Tap to change pincode</div>
                  </>
                )}
              </div>
            </button>

            {/* Stock alert */}
            {hasStockIssue && (
              <div className="qd-card p-3 border-2 border-red-200 bg-red-50/60 text-sm" data-testid="cart-stock-alert">
                <div className="font-heading font-black text-red-700">Inventory updated</div>
                <div className="text-xs text-gray-700 mt-1">
                  Some items are out of stock or only have limited stock left. Update qty or remove them to continue.
                </div>
              </div>
            )}

            {/* Items */}
            <div className="qd-card divide-y divide-gray-100">
              {cart.items.map((it) => {
                const avail = liveInv[it.product_id];
                const isOos = avail === 0;
                const isLow = typeof avail === "number" && avail > 0 && it.qty > avail;
                const atMax = typeof avail === "number" && it.qty >= avail;
                return (
                  <div key={it.product_id}
                    className={`p-3 flex items-center gap-3 ${isOos ? "bg-red-50/30" : ""}`}
                    data-testid={`cart-item-${it.product_id}`}>
                    <div className="w-14 h-14 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
                      {it.image && <img src={it.image} alt="" className={`w-full h-full object-cover ${isOos ? "grayscale" : ""}`} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold line-clamp-1">{it.name}</div>
                      <div className="font-heading font-black">₹{it.price * it.qty}</div>
                      {isOos && (
                        <div className="text-[10px] text-red-600 font-bold mt-0.5" data-testid={`cart-item-oos-${it.product_id}`}>
                          Out of stock — please remove
                        </div>
                      )}
                      {isLow && (
                        <div className="text-[10px] text-amber-700 font-bold mt-0.5" data-testid={`cart-item-low-${it.product_id}`}>
                          Only {avail} quantity available
                        </div>
                      )}
                    </div>
                    {!isOos && (
                      <div className="flex items-center gap-1 bg-[#15803D] text-white rounded-lg overflow-hidden">
                        <button onClick={() => decrement(slug, it.product_id)} className="px-2 py-2"><Minus size={14} weight="bold" /></button>
                        <span className="text-sm font-bold min-w-[24px] text-center">{it.qty}</span>
                        <button
                          onClick={() => {
                            if (atMax) { toast.error(`Only ${avail} quantity available`); return; }
                            add(slug, store.id, { id: it.product_id, name: it.name, price: it.price, images: [it.image], inventory: avail });
                          }}
                          disabled={atMax}
                          className={`px-2 py-2 ${atMax ? "opacity-50 cursor-not-allowed" : ""}`}><Plus size={14} weight="bold" /></button>
                      </div>
                    )}
                    <button onClick={() => remove(slug, it.product_id)} className="text-red-500 ml-1" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Bill */}
            <div className="qd-card p-4 space-y-2 text-sm">
              <h3 className="font-heading font-bold text-base mb-2">Bill details</h3>
              <Row label="Subtotal" value={`₹${sub}`} />
              <Row label="Delivery charge" value={charge === 0 ? (serviceable ? "FREE" : "—") : `₹${charge}`} accent={charge === 0 && serviceable} />
              {freeAbove > 0 && sub < freeAbove && serviceable && (
                <div className="text-xs text-[#15803D] bg-[#15803D]/10 rounded-lg p-2">
                  Add ₹{freeAbove - sub} more for FREE delivery!
                </div>
              )}
              {minOrder > 0 && sub < minOrder && (
                <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2" data-testid="cart-min-order-hint">
                  Add ₹{minOrder - sub} more to reach the minimum order (₹{minOrder})
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between font-heading font-black text-base">
                <span>Total</span><span>₹{total}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sticky checkout */}
      {cart.items.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 p-3 bg-white border-t border-gray-100 z-30">
          <button onClick={onCheckoutClick}
            disabled={buttonDisabled}
            className="w-full max-w-2xl mx-auto qd-btn-green disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed flex items-center justify-between"
            data-testid="cart-checkout-btn">
            <div className="text-left">
              <div className="text-xs opacity-90">₹{total} · {cart.items.length} item{cart.items.length > 1 && "s"}</div>
            </div>
            <div data-testid="cart-checkout-label">{buttonLabel}</div>
          </button>
        </div>
      )}

      {showPinModal && (
        <PincodeModal
          initial={pincode}
          onClose={() => setShowPinModal(false)}
          onSave={(pin) => {
            setPincode(pin);
            setShowPinModal(false);
          }}
        />
      )}

      <GuestLoginPrompt returnTo={`/${slug}/checkout`} />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-600">{label}</span>
      <span className={accent ? "text-[#15803D] font-bold" : "font-semibold"}>{value}</span>
    </div>
  );
}
